"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Building2, House as Home, ShieldCheck, Hammer, ArrowRight, HardHat, Layers, Flame, CircleCheck as CheckCircle2, Wrench, Umbrella, Shield } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const coverageAreas = [{
  title: "Town-centre commercial",
  desc: "Pedestrian gantries, pavement licences, and out-of-hours erection."
}, {
  title: "Office developments",
  desc: "Access towers, perimeter scaffolding, and internal birdcages."
}, {
  title: "Residential streets",
  desc: "Neat, considerate setups with protected driveways and gardens."
}, {
  title: "Refurbishment & mixed-use",
  desc: "Phased adaptations for occupied premises and ongoing trade work."
}, {
  title: "Roof & chimney projects",
  desc: "Stable working platforms with full handrails and weather protection."
}];
const residentialFeatures = ["Roof repairs & re-tiling access", "Single & multi-storey extensions", "Chimney stack repairs & repointing", "External rendering & painting access", "Fascias, soffits & gutter replacement"];
const commercialFeatures = ["Main contractor & developer frameworks", "Commercial refurbishment & fit-outs", "Fully occupied site management", "Edge protection & heavy-duty loading bays", "TG20:21 compliant designs & handovers"];
const tradeAccessCards = [{
  title: "Roofing scaffolding",
  description: "Dedicated eaves-level staging, hop-up platforms, and debris netting for roofing teams.",
  to: "/RoofingScaffoldingPage",
  icon: HardHat
}, {
  title: "Temporary roofs",
  description: "Complete weather protection systems allowing structural works to continue through rain.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella
}, {
  title: "Edge protection",
  description: "Robust guardrails, toe-boards, and barrier systems preventing falls from height.",
  to: "/EdgeProtectionPage",
  icon: Shield
}];
export default function FleetServices() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".gsap-fade", {
        y: 28
      });
      gsap.to(".gsap-fade", {
        opacity: 1,
        y: 0,
        duration: 0.65,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".gsap-fade", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="fleetscaffoldingservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div ref={containerRef} className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="gsap-fade inline-flex items-center gap-2 mb-3" style={{
          opacity: 0
        }}>
            <Badge variant="outline" className="border-border text-foreground px-3 py-1 font-medium">
              Fleet & Hampshire
            </Badge>
            <span className="text-xs text-muted-foreground font-medium">
              NASC • CHAS
            </span>
          </div>
          <h2 className="gsap-fade font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground" style={{
          opacity: 0
        }}>
            Scaffolding services across Fleet and surrounding areas
          </h2>
          <p className="gsap-fade text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed" style={{
          opacity: 0
        }}>
            From town-centre commercial premises to residential extensions and roof repairs, we design,
            erect, and inspect compliant access structures for all building types.
          </p>
        </div>

        {/* Coverage Strip */}
        <div className="gsap-fade mb-16" style={{
        opacity: 0
      }}>
          <div className="rounded-xl border border-border bg-card p-6 md:p-8 shadow-xs">
            <h3 className="font-default text-lg font-semibold text-foreground mb-4">
              Property types & project scopes we support in Fleet
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {coverageAreas.map((area, i) => <div key={area.title} data-index={i} className="rounded-lg bg-muted/40 p-4 border border-border/50 flex flex-col justify-between">
                  <span className="font-semibold text-sm text-foreground mb-1">{area.title}</span>
                  <span className="text-xs text-muted-foreground leading-normal">{area.desc}</span>
                </div>)}
            </div>
          </div>
        </div>

        {/* Two Main Columns: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
          {/* Residential Subsection */}
          <Card className="gsap-fade flex flex-col justify-between border-border shadow-xs hover:border-primary/40 transition-colors" style={{
          opacity: 0
        }}>
            <CardHeader className="space-y-3 pb-4">
              <div className="flex items-center justify-between">
                <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Home className="size-6" />
                </div>
                <Badge variant="secondary" className="text-xs">
                  Homeowners & Builders
                </Badge>
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Residential scaffolding in Fleet
              </CardTitle>
              <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                Safe, clean working platforms for domestic renovations, chimney repairs, and external property maintenance. We prioritise punctuality, tidy dismantling, and property protection.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-2.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Common residential requirements
                </p>
                <ul className="space-y-2 text-sm text-foreground">
                  {residentialFeatures.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </div>

              <Separator className="bg-border" />

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild variant="default" className="w-full sm:w-auto">
                  <Link to="/ResidentialScaffoldingPage">
                    Residential Scaffolding
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/ChimneyScaffoldingPage">
                    Chimney Scaffolding
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Commercial Subsection */}
          <Card className="gsap-fade flex flex-col justify-between border-border shadow-xs hover:border-primary/40 transition-colors" style={{
          opacity: 0
        }}>
            <CardHeader className="space-y-3 pb-4">
              <div className="flex items-center justify-between">
                <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Building2 className="size-6" />
                </div>
                <Badge variant="secondary" className="text-xs">
                  Contractors & Facilities
                </Badge>
              </div>
              <CardTitle className="text-2xl font-bold text-foreground">
                Commercial scaffolding in Fleet
              </CardTitle>
              <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                Engineered access solutions for main contractors, local authorities, retail premises, and commercial landlords. Rigorous health and safety documentation on every site.
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <div className="space-y-2.5">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Commercial capabilities
                </p>
                <ul className="space-y-2 text-sm text-foreground">
                  {commercialFeatures.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-2.5">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>)}
                </ul>
              </div>

              <Separator className="bg-border" />

              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <Button asChild variant="default" className="w-full sm:w-auto">
                  <Link to="/CommercialScaffoldingPage">
                    Commercial Scaffolding
                    <ArrowRight className="size-4 ml-1" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/MainContractorsDevelopersPage">
                    Main Contractors & Developers
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Roofing & Trade Access Block */}
        <div className="gsap-fade rounded-2xl bg-card border border-border p-6 sm:p-8 lg:p-10 shadow-sm" style={{
        opacity: 0
      }}>
          <div className="max-w-2xl mb-8">
            <div className="inline-flex items-center gap-1.5 text-primary text-sm font-semibold mb-2">
              <Layers className="size-4" />
              <span>Specialist Trades</span>
            </div>
            <h3 className="font-default text-2xl sm:text-3xl font-bold text-foreground">
              Roofing & trade access
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base mt-2">
              Purpose-built scaffolding configurations for roofers, solar installers, and maintenance teams requiring protected, high-level working platforms.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {tradeAccessCards.map((card, i) => {
            const IconComp = card.icon;
            return <div key={card.title} data-index={i} className="rounded-xl border border-border bg-background p-6 flex flex-col justify-between hover:shadow-xs transition-shadow">
                  <div>
                    <div className="size-10 rounded-md bg-muted flex items-center justify-center text-foreground mb-4">
                      <IconComp className="size-5" />
                    </div>
                    <h4 className="font-default font-semibold text-lg text-foreground mb-2">
                      {card.title}
                    </h4>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {card.description}
                    </p>
                  </div>
                  <div className="pt-6">
                    <Button asChild variant="link" className="px-0 h-auto font-medium text-primary">
                      <Link to={card.to} className="inline-flex items-center gap-1.5">
                        <span>View {card.title.toLowerCase()}</span>
                        <ArrowRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>;
          })}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-6 border-t border-border">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="size-4 text-primary shrink-0" />
              <span>Safety inspections, handover certificates, and full TG20 compliance included.</span>
            </div>
            <Button asChild size="default" className="w-full sm:w-auto shrink-0">
              <Link to="/ContactRequestQuotePage">
                Request a quote for Fleet
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}